import { z } from "zod";
import { evaluateDeadline } from "./deadline";
import { onlyDigits, parseBRL } from "./masks";
import { hasFullName, isValidCPF } from "./validators";

const step1Fields = {
  incorporadora: z.string().min(1, "Selecione a rede/incorporadora."),
  incorporadoraOutra: z.string(),
  dataAssinatura: z
    .string()
    .min(1, "Informe a data de assinatura.")
    .refine((v) => evaluateDeadline(v).status !== "invalid", "Data inválida.")
    .refine((v) => evaluateDeadline(v).status !== "future", "A data de assinatura não pode ser futura."),
  formaPagamento: z.string().min(1, "Selecione a forma de pagamento."),
  valorEntrada: z.string().refine((v) => parseBRL(v) > 0, "Informe um valor maior que zero."),
};

export const holderFields = {
  email: z.string().trim().min(1, "Informe seu e-mail.").email("E-mail inválido."),
  whatsapp: z.string().refine((v) => {
    const n = onlyDigits(v).length;
    return n >= 10 && n <= 11;
  }, "WhatsApp inválido. Use o formato (00) 00000-0000."),
  aceite: z.boolean().refine((v) => v, "É necessário aceitar os Termos de Uso."),
};

const step2Strict = {
  nomeCompleto: z.string().min(1, "Informe seu nome completo.").refine(hasFullName, "Informe nome e sobrenome."),
  cpf: z.string().min(1, "Informe seu CPF.").refine(isValidCPF, "CPF inválido. Verifique os dígitos."),
  email: z.string().trim().min(1, "Informe seu e-mail.").email("E-mail inválido."),
  whatsapp: z.string().refine((v) => {
    const n = onlyDigits(v).length;
    return n >= 10 && n <= 11;
  }, "WhatsApp inválido. Use o formato (00) 00000-0000."),
  aceite: z.boolean().refine((v) => v, "É necessário aceitar os Termos de Uso."),
};

const step2Loose = {
  nomeCompleto: z.string(),
  cpf: z.string(),
  email: z.string(),
  whatsapp: z.string(),
  aceite: z.boolean(),
};

const refineOutra = (data: { incorporadora: string; incorporadoraOutra: string }, ctx: z.RefinementCtx): void => {
  if (data.incorporadora === "Outra" && data.incorporadoraOutra.trim().length < 2) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["incorporadoraOutra"], message: "Informe o nome da incorporadora." });
  }
};

export const step1Schema = z.object({ ...step1Fields, ...step2Loose }).superRefine(refineOutra);
export const contractSchema = z.object({ ...step1Fields, ...step2Strict }).superRefine(refineOutra);

export type ContractFormData = z.infer<typeof contractSchema>;
export type ContractFieldName = keyof ContractFormData;

export const emptyContract: ContractFormData = {
  incorporadora: "",
  incorporadoraOutra: "",
  dataAssinatura: "",
  formaPagamento: "",
  valorEntrada: "",
  nomeCompleto: "",
  cpf: "",
  email: "",
  whatsapp: "",
  aceite: false,
};

export const isContractField = (name: string): name is ContractFieldName => name in emptyContract;

export const STEP1_FIELDS: ContractFieldName[] = ["incorporadora", "incorporadoraOutra", "dataAssinatura", "formaPagamento", "valorEntrada"];
